import { Stack } from "@mui/system";

import { useMemo, useRef, useState } from "react";

import { Typography, useTheme } from "@mui/material";

import { useDeviceDimensions } from "igfd-ui/src";

import Logo from "../../assets/logo.svg";
import { useRestaurantContext } from "../../hooks/useRestaurantContext";
import type { MenuTreeNode } from "../../menu/menu-tree";
import { staffMenuCopy } from "../../menu/staff-menu-copy";
import {
  type StaffListRow,
  buildStaffMenuRows,
  searchStaffMenu,
  staffDishNode,
} from "../../menu/staff-menu-tree";
import { trackStaffEvent } from "../../monitoring/mixpanel";
import { useSearchTracking } from "../../monitoring/useSearchTracking";
import { DishDrawer } from "../dish-drawer/dish-drawer";
import { MenuSpecialNote } from "../menu-special-note/menu-special-note";
import { ProductDrawer } from "../product-drawer/product-drawer";
import { Dish } from "./components/dish-item";
import { GroupRow } from "./components/group-row";

interface DishListingProps {
  filter: string;
}

type Section = {
  id: string;
  name: string;
  rows: StaffListRow[];
};

export const DishListing = ({ filter }: DishListingProps) => {
  const {
    palette: { oscar },
  } = useTheme();
  const [selectedNode, setSelectedNode] = useState<MenuTreeNode>();
  const [selectedProductId, setSelectedProductId] = useState<string>();
  const { menu, linkedItems } = useRestaurantContext();
  const dishOpenedAt = useRef(0);
  const { isMobile } = useDeviceDimensions();

  /**
   * Searching reaches the options folded into their dishes, so results are one
   * flat list carrying their own trail rather than the section-grouped menu.
   */
  const results = useMemo(() => searchStaffMenu(menu, filter), [menu, filter]);
  useSearchTracking("menu", filter, results.length);

  const openNode = (node: MenuTreeNode, source: "menu" | "search") => {
    if (node.dish) {
      trackStaffEvent("Dish Opened", {
        dish_id: node.dish.id,
        dish_name: node.dish.name,
        source,
      });
    }
    dishOpenedAt.current = Date.now();
    setSelectedNode(node);
  };

  const closeNode = () => {
    if (selectedNode?.dish) {
      // How long it took to answer: from opening the dish to closing it.
      trackStaffEvent("Dish Closed", {
        dish_id: selectedNode.dish.id,
        duration_seconds: Math.round(
          (Date.now() - dishOpenedAt.current) / 1000
        ),
      });
    }
    setSelectedNode(undefined);
  };

  const openProduct = (productId: string) => {
    trackStaffEvent("Product Opened", {
      product_id: productId,
      product_name: linkedItems?.products[productId]?.name,
    });
    setSelectedProductId(productId);
  };

  const sections = useMemo<Section[]>(() => {
    if (filter) return [];
    return (menu?.menuSectionsInDisplayOrder ?? []).reduce<Section[]>(
      (acc, section) => {
        const built = buildStaffMenuRows(section);
        // A section with no structural order, or one whose order did not
        // parse, still renders every dish it has.
        const rows: StaffListRow[] =
          built.kind === "rows"
            ? built.rows
            : (section.dishesInDisplayOrder ?? []).map((dish) => ({
                kind: "dish" as const,
                id: dish.id,
                depth: 0,
                node: staffDishNode(dish),
                dish,
                choiceCount: 0,
                unreadableCount: 0,
              }));

        if (!rows.length) return acc;
        return [
          ...acc,
          { id: section.id, name: section.menuSectionName, rows },
        ];
      },
      []
    );
  }, [menu, filter]);

  return (
    <Stack
      alignItems="center"
      bgcolor={oscar.light.surface.neutral}
      display="flex"
      justifyContent="flex-start"
      flexGrow={1}
      paddingX={isMobile ? 1 : 2}
      paddingY={isMobile ? 2 : 4}
    >
      {menu?.notes?.trim() ? <MenuSpecialNote notes={menu.notes} /> : null}
      <Stack
        alignSelf="center"
        bgcolor={oscar.light.surface.clear}
        border={`1px solid ${oscar.light.borders.gray}`}
        borderRadius={isMobile ? 4 : 6}
        paddingX={isMobile ? 3 : 9}
        paddingY={isMobile ? 3 : 6}
        width="100%"
        maxWidth={728}
      >
        {filter
          ? results.map((result, index) => (
              <Dish
                key={result.id}
                title={result.dish.name}
                highlight={filter}
                description={result.dish.description}
                trail={result.trail}
                hasDivider={index < results.length - 1}
                onClick={() => openNode(result.node, "search")}
              />
            ))
          : sections.map((section, sectionIndex) => (
              <Stack
                key={section.id}
                paddingBottom={sectionIndex < sections.length - 1 ? 6 : 0}
              >
                <Typography variant="headingRMedium">{section.name}</Typography>
                <Stack>
                  {section.rows.map((row, index) => {
                    const hasDivider = index < section.rows.length - 1;

                    if (row.kind === "group") {
                      return (
                        <GroupRow
                          key={row.id}
                          title={row.title}
                          description={row.description}
                          required={row.required}
                          optionCount={row.optionCount}
                          groupCount={row.groupCount}
                          indent={row.depth}
                          hasDivider={hasDivider}
                          onClick={() => openNode(row.node, "menu")}
                        />
                      );
                    }

                    return (
                      <Dish
                        key={row.id}
                        title={row.dish.name}
                        description={row.dish.description}
                        choiceCount={row.choiceCount}
                        unreadableCount={row.unreadableCount}
                        indent={row.depth}
                        hasDivider={hasDivider}
                        onClick={() => openNode(row.node, "menu")}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            ))}
      </Stack>

      <Stack alignItems="center" marginTop={10} marginBottom={3}>
        <img src={Logo} alt="Ingredifind" style={{ width: 130 }} />
      </Stack>

      <DishDrawer
        open={!!selectedNode}
        node={selectedNode}
        onClose={closeNode}
        onSelectProduct={openProduct}
      />
      <ProductDrawer
        open={!!selectedProductId}
        productId={selectedProductId}
        onClose={() => setSelectedProductId(undefined)}
      />
    </Stack>
  );
};
